import { supabase } from '../lib/supabase';

export const RESOURCE_FILES_BUCKET = 'gfi-resource-files';
export const BLOG_IMAGES_BUCKET = 'gfi-blog-images';
export const PROFILE_PHOTOS_BUCKET = 'gfi-profile-photos';

const SIGNED_URL_TTL_SECONDS = 3600;

function sanitizeFileName(name) {
  const base = name.replace(/[/\\?%*:|"<>]/g, '-').trim();
  return base || 'file';
}

export function buildResourceStoragePath(resourceId, versionId, fileName) {
  return `${resourceId}/${versionId}/${sanitizeFileName(fileName)}`;
}

/**
 * Upload a Resource Hub document to Supabase Storage.
 * Caller must create the parent gfi_resources row first so storage RLS can verify access.
 */
export async function uploadResourceFile(resourceId, versionId, file) {
  const storagePath = buildResourceStoragePath(resourceId, versionId, file.name);

  const { error } = await supabase.storage
    .from(RESOURCE_FILES_BUCKET)
    .upload(storagePath, file, {
      cacheControl: '3600',
      upsert: false,
      contentType: file.type || undefined,
    });

  if (error) throw error;
  return storagePath;
}

export async function createResourceSignedUrls(storagePath) {
  if (!storagePath?.trim()) return { fileUrl: null, previewUrl: null };

  const [downloadResult, previewResult] = await Promise.all([
    supabase.storage.from(RESOURCE_FILES_BUCKET).createSignedUrl(
      storagePath,
      SIGNED_URL_TTL_SECONDS,
      { download: true },
    ),
    supabase.storage.from(RESOURCE_FILES_BUCKET).createSignedUrl(
      storagePath,
      SIGNED_URL_TTL_SECONDS,
      { download: false },
    ),
  ]);

  if (downloadResult.error) throw downloadResult.error;
  if (previewResult.error) throw previewResult.error;

  return {
    fileUrl: downloadResult.data?.signedUrl ?? null,
    previewUrl: previewResult.data?.signedUrl ?? null,
  };
}

export async function enrichVersionWithStorageUrls(version) {
  if (!version?.storage_path) return version;

  const urls = await createResourceSignedUrls(version.storage_path);
  return {
    ...version,
    file_url: urls.fileUrl,
    preview_url: urls.previewUrl,
  };
}

export async function enrichVersionsWithStorageUrls(versions) {
  const rows = versions ?? [];
  return Promise.all(rows.map((v) => enrichVersionWithStorageUrls(v)));
}
